import { USER_COMPANY_ROLES } from '@/constants/user'
import { ConfigMap, MapperType } from '@/lib/types/utilsT'
import { MemberActionTypes, UserCompanyRoleTypes } from '@/lib/types/UserT'
import { IconTypes } from '@/components/Icon'
import { VCardBulkActionTypes, VCardOrderOption } from '@/lib/types/VCardT'
import { CompaniesOrderOption } from '@/lib/types/CompanyT'
import { VCardFormSchema } from '@/lib/schemas/authUser/vCard'
import { THEME_MODES } from '@/constants/VCard'
import { UNLIMITED_THRESHOLD } from '@/constants/subscription'

export const ONBOARDING_SESSION_STORAGE_KEY = 'onboarding_storage'
export const HAS_SEEN_CONTACT_PROFILE_GUIDE_KEY = 'hasSeenContactProfileGuide'
export const LANDING_CONTACT_PREVIEW_TAB_KEY = 'landingContactPreviewTab'

export const INPUT_TYPES = {
  FIELD: 'field',
  COLOR: 'color',
  DUAL_COLOR: 'dual-color',
  FILE: 'file',
  OTP: 'otp',
  SELECT: 'select',
  MULTI_SELECT: 'multi-select',
  USER_SELECT: 'user-select',
  TAGS: 'tags',
  PHONE_FIELD: 'phone-field',
  STEPPER: 'stepper',
  CHECK_BOX: 'check-box',
  MULTI_VALUE_FIELD: 'multi-value-field',
  DATE_RANGE: 'date-range-field',
  TIMEZONE_SELECT: 'timezone-select',
  VCARD_MULTI_SELECT_FIELD: 'vcard-multi-select-field',
  CONTACT_SELECTOR: 'contact-selector',
} as const

// Allowed image MIME types
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

// Allowed CSV/Excel MIME types
export const ACCEPTED_CSV_TYPES = ['text/csv']
export const ACCEPTED_EXCEL_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]
export const ACCEPTED_SPREADSHEET_TYPES = [...ACCEPTED_CSV_TYPES, ...ACCEPTED_EXCEL_TYPES]

export const MAX_FILE_SIZE_5_MB = 5 * 1024 * 1024

export const VCARD_ORDER_OPTIONS: VCardOrderOption[] = [
  { value: 'updated_at', title: 'Recently Updated', orderDirection: 'desc' },
  { value: 'created_at', title: 'Recently Created', orderDirection: 'desc' },
  { value: 'representative_name', title: 'Name (A-Z)', orderDirection: 'asc' },
]

export const COMPANIES_ORDER_OPTIONS: CompaniesOrderOption[] = [
  { value: 'name', title: 'Name (A-Z)', orderDirection: 'asc' },
  { value: 'email', title: 'Email (A-Z)', orderDirection: 'asc' },
  { value: 'created_at', title: 'Recently Created', orderDirection: 'desc' },
]

export type CompanyContactOrderOption = {
  value: 'joined_company_at' | 'name'
  title: string
  orderDirection: 'asc' | 'desc'
}

export const COMPANY_CONTACTS_ORDER_OPTIONS: CompanyContactOrderOption[] = [
  {
    value: 'joined_company_at',
    title: 'Recently Joined',
    orderDirection: 'desc',
  },
  {
    value: 'name',
    title: 'Name (A–Z)',
    orderDirection: 'asc',
  },
]

export const VCARDS_BULK_ACTIONS: {
  type: VCardBulkActionTypes
  label: string
  className: string
}[] = [
  {
    type: 'attach-manager',
    label: 'Attach Manager',
    className: 'btn-stroke btn-small sm:text-xs sm:px-2 sm:py-1',
    // className: 'btn-red btn-small sm:text-xs sm:px-2 sm:py-1 hover:border hover:translate-y-0',
  },
]

export const SIGN_IN_PAGE_TAB_INDEX = {
  SIGN_IN: 0,
  CREATE_ACCOUNT: 1,
}

export const DEFAULT_ERROR_MESSAGES: Record<number, string> = {
  401: 'Unauthorized. Please log in.',
  403: 'You do not have permission to perform this action.',
  409: 'Conflict. This resource already exists.',
  422: 'Validation error. Please check your input.',
  500: 'Internal server error. Please try again later.',
}

export type MemberActionType = MapperType<MemberActionTypes>
export const MEMBER_ACTIONS_ROLES = {
  MAKE_ADMIN: 'make-admin',
  MAKE_MANAGER: 'make-manager',
  REMOVE: 'remove',
} satisfies MemberActionType

export const MEMBER_ACTIONS_ROLES_CONFIG: ConfigMap<
  MemberActionTypes,
  {
    label: string
    icon: IconTypes
    availableRoles: UserCompanyRoleTypes[]
    hiddenForRoles?: UserCompanyRoleTypes[]
  }
> = {
  [MEMBER_ACTIONS_ROLES.MAKE_ADMIN]: {
    label: 'Make Admin',
    icon: 'star',
    availableRoles: [USER_COMPANY_ROLES.OWNER, USER_COMPANY_ROLES.LIVECONTACT_SUPPORT],
    hiddenForRoles: [USER_COMPANY_ROLES.ADMIN, USER_COMPANY_ROLES.OWNER],
  },
  [MEMBER_ACTIONS_ROLES.MAKE_MANAGER]: {
    label: 'Make Manager',
    icon: 'profile',
    availableRoles: [
      USER_COMPANY_ROLES.OWNER,
      USER_COMPANY_ROLES.ADMIN,
      USER_COMPANY_ROLES.LIVECONTACT_SUPPORT,
    ],
    hiddenForRoles: [
      USER_COMPANY_ROLES.MANAGER,
      USER_COMPANY_ROLES.OWNER,
      USER_COMPANY_ROLES.LIVECONTACT_SUPPORT,
    ],
  },
  [MEMBER_ACTIONS_ROLES.REMOVE]: {
    label: 'Delete Member',
    icon: 'trash',
    availableRoles: [
      USER_COMPANY_ROLES.OWNER,
      USER_COMPANY_ROLES.ADMIN,
      USER_COMPANY_ROLES.LIVECONTACT_SUPPORT,
    ],
    hiddenForRoles: [USER_COMPANY_ROLES.OWNER, USER_COMPANY_ROLES.LIVECONTACT_SUPPORT],
  },
}

export const VCARD_HERO_LANDING_PREVIEW: VCardFormSchema = {
  representativeName: 'LiveContact',
  notes:
    'The AI-Powered Contact Card That Makes SMS Marketing Obsolete. Create contact cards people actually keep, collect numbers, send texts, and stay top of mind.',
  avatar: '/images/avatar.jpg',
  mainColor: '#0084FF',
  industry: 'Technology & SaaS Platform',
  emails: [{ alias: '', value: 'hello@livecontact.com' }],
  website: 'https://livecontact.com',
  addresses: [
    {
      label: 'Office',
      street1: '123 Innovation Drive',
      street2: '',
      city: 'San Francisco',
      state: 'CA',
      postal: '94105',
      country: 'United States',
    },
  ],
  unverifiedPhones: [],
  defaultLinks: [],
  socialLinks: [
    { type: 'linkedin', url: 'LiveContact' },
    { type: 'x', url: '@livecontact' },
    { type: 'instagram', url: '@livecontact' },
    { type: 'facebook', url: 'LiveContact' },
  ],
  companyFileItems: [],
}

/**
 * The brokerage the mortgage-broker landing shows in its hero panel. It's a real, recognisable
 * lender rather than our own card, so a broker landing on the page sees the assets built for
 * someone in their business instead of a demo of ours. The description is the full one their
 * site publishes — long on purpose, so the card's clamp and "Read more" are shown doing their
 * job rather than hidden behind a copy-fitted blurb.
 */
export const VCARD_BROKER_HERO_PREVIEW: VCardFormSchema = {
  representativeName: 'Rocket',
  notes:
    'Rocket Mortgage is a digital mortgage lender that streamlines home financing through an online platform. It offers a full suite of loan products — including purchase, refinance, home-equity and personal loans — catering to first-time buyers, veterans, self-employed borrowers and investors. Users can browse listings, calculate payments, receive real-time rate alerts and lock rates, all from their kitchen table. The service integrates with partner real-estate agents to provide cash-back incentives and reduced commissions. With over 9 million mortgages closed, Rocket Mortgage emphasizes speed, transparency and flexible options, helping customers buy, refinance or unlock equity while managing debt and maximizing savings.',
  avatar: '/images/rocket-logo.jpg',
  banner: '/images/rocket-banner.jpg',
  mainColor: '#DB3544',
  industry: 'Banking & Lending',
  emails: [{ alias: '', value: 'help@rocket.com' }],
  website: 'https://rocket.com',
  addresses: [
    {
      label: 'Office',
      street1: '1050 Woodward Ave.',
      street2: '',
      city: 'Detroit',
      state: 'MI',
      postal: '48226',
      country: 'United States',
    },
  ],
  unverifiedPhones: [{ label: 'Main', phone: '+1 800 980 5022' }],
  defaultLinks: [],
  socialLinks: [
    { type: 'linkedin', url: 'rocket' },
    { type: 'instagram', url: '@rocketofficial' },
    { type: 'facebook', url: 'rocket' },
    { type: 'youtube', url: '@rocketmortgage' },
  ],
  companyFileItems: [],
}

/**
 * The restaurant the hospitality landing shows in its hero panel — a real, 80-year-old deli
 * rather than our own card, so an operator sees the assets built for a business like theirs.
 * Avatar, banner and colours are the ones the brand actually publishes (the banner is their own
 * social/OG image), and the description is the long one from their site so the card's clamp and
 * "Read more" are shown doing their job. No phone or email: they publish neither, and the card
 * only ever shows the facts it holds.
 */
export const VCARD_RESTAURANT_HERO_PREVIEW: VCardFormSchema = {
  representativeName: "Manny's Deli",
  notes:
    "Manny's Deli is Chicago's historic cafeteria-style Jewish deli, operating since 1942. They serve iconic, hand-carved sandwiches, fresh bagels, homemade soups and classic deli sides in a bustling cafeteria setting. In addition to on-site dining, Manny's offers nationwide shipping of their signature sandwiches and baked goods, a full catering service for offices and events, private-event space for celebrations, and an online shop featuring merchandise, wholesale items and a cookie shop. Their menu highlights include the legendary corned-beef sandwich, bagels with lox, and hearty lunch platters, delivering an authentic Chicago deli experience to locals and customers across the country.",
  avatar: '/images/mannys-logo.png',
  banner: '/images/mannys-banner.jpg',
  mainColor: '#C41230',
  industry: 'Restaurants & Food Service',
  emails: [],
  website: 'https://www.mannysdeli.com',
  addresses: [
    {
      label: 'Deli',
      street1: '1141 S Jefferson St',
      street2: '',
      city: 'Chicago',
      state: 'IL',
      postal: '60607',
      country: 'United States',
    },
  ],
  unverifiedPhones: [{ label: 'Main', phone: '+1 312 939 2855' }],
  defaultLinks: [],
  socialLinks: [
    { type: 'instagram', url: '@mannysdeli' },
    { type: 'facebook', url: 'mannyschicago' },
    { type: 'x', url: '@mannysdeli' },
    { type: 'tiktok', url: '@mannysdeli' },
  ],
  companyFileItems: [],
}

export const DEFAULT_QR_COLORS = {
  [THEME_MODES.LIGHT]: { fg: '#1A1D1F', bg: '#F4F4F6' },
  [THEME_MODES.DARK]: { fg: '#FCFCFC', bg: '#272B30' },
}

export const ONBOARDING_BUSINESS_SIZE_CONFIG = [
  { label: 'Personal (just me)', description: 'Individual contact card', maxSize: 1 },
  {
    label: 'Business (<10 people)',
    description: 'Perfect for startups and small teams',
    maxSize: 10,
  },
  { label: 'Mid-size (10-100 people)', description: 'Growing businesses', maxSize: 100 },
  {
    label: 'Enterprise (100+ people)',
    description: 'Large organizations',
    maxSize: UNLIMITED_THRESHOLD,
  },
]

export const PHONE_NUMBER_FIELD_SIZE_CONFIG = {
  sm: {
    container: 'p-3 rounded-xl',
    header: 'mb-2',
    title: 'text-xs',
    icon: 'w-3 h-3',
    spacing: 'space-y-2',
    input: 'h-8 text-xs',
    label: 'text-xs mb-0.5',
    labelInner: 'text-xs',
    placeholder: 'text-xs',
  },
  md: {
    container: 'p-4 rounded-xl md:p-3',
    header: 'mb-3',
    title: 'text-sm',
    icon: 'w-4 h-4',
    spacing: 'space-y-3',
    input: 'h-10 text-sm',
    label: 'text-xs mb-1',
    labelInner: 'text-sm',
    placeholder: 'text-sm',
  },
  lg: {
    container: 'p-6 rounded-2xl md:p-4',
    header: 'mb-4',
    title: 'text-base',
    icon: 'w-5 h-5',
    spacing: 'space-y-4',
    input: 'h-12 text-base',
    label: 'text-sm mb-1.5',
    labelInner: 'text-base',
    placeholder: 'text-base',
  },
}

export const LINK_ITEM_SIZE_CONFIG = {
  sm: {
    container: 'p-3 rounded-xl',
    header: 'mb-2',
    title: 'text-xs',
    icon: 'w-3 h-3',
    spacing: 'space-y-2',
    input: 'h-8 text-xs pr-7',
    label: 'text-xs mb-0.5',
    labelInner: 'text-xs',
    placeholder: 'text-xs',
  },
  md: {
    container: 'p-4 rounded-xl md:p-3',
    header: 'mb-3',
    title: 'text-sm',
    icon: 'w-4 h-4',
    spacing: 'space-y-3',
    input: 'h-10 text-sm pr-8',
    label: 'text-xs mb-1',
    labelInner: 'text-sm',
    placeholder: 'text-sm',
  },
  lg: {
    container: 'p-6 rounded-2xl md:p-4',
    header: 'mb-4',
    title: 'text-base',
    icon: 'w-5 h-5',
    spacing: 'space-y-4',
    input: 'h-12 text-base pr-9',
    label: 'text-sm mb-1.5',
    labelInner: 'text-base',
    placeholder: 'text-base',
  },
}

export const USER_AVATAR_SIZES_CLASS = {
  sm: {
    avatar: 'w-5 h-5',
    text: 'text-[9px]',
  },
  md: {
    avatar: 'w-6 h-6',
    text: 'text-xs',
  },
  lg: {
    avatar: 'w-8 h-8',
    text: 'text-sm',
  },
}
